// src/pages/Consignacion/Tabs/TabPrecios.js
//
// Carga masiva de precios para los artículos de consignación.
//
// El precio de la lista de consignación es el que valoriza el stock y el que
// sale en la orden de reposición: un artículo sin precio entra a la orden en 0 y
// hay que corregirlo a mano en el borrador.
//
// La carga es en dos pasos a propósito. Un precio mal interpretado —"1,234" son
// mil doscientos treinta y cuatro o uno con veintitrés según el formato— no da
// error: deja el artículo con un precio equivocado. Por eso primero se simula
// contra el backend y se muestra qué número entendió de cada fila.
import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../../../services/api';
import { parseExcelFile } from '../../../utils/excelParser';
import { useToast } from '../../../components/Toast/ToastProvider';
import { useAuth } from '../../../hooks/useAuth';
import { formatMoney, formatNumber } from '../../../utils/formato';
import '../Consignacion.css';

// Encabezados que se reconocen, en orden de preferencia. Son los mismos del
// listado que exporta SAP, para poder subirlo sin tocarlo.
const COL_CODIGO = ['Número de artículo', 'Numero de articulo', 'ItemCode', 'Codigo', 'Código', 'Articulo', 'Artículo'];
const COL_PRECIO = ['Precio por UM de inventario', 'Precio', 'Price', 'Precio unitario'];

/** Busca en la fila la primera columna que coincida, sin distinguir acentos. */
const normalizar = (t) => String(t || '').trim().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '');

const buscarColumna = (fila, candidatas) => {
    const claves = Object.keys(fila);
    for (const candidata of candidatas) {
        const encontrada = claves.find(k => normalizar(k) === normalizar(candidata));
        if (encontrada) return encontrada;
    }
    return null;
};

const TabPrecios = () => {
    const { showToast } = useToast();
    const { puedeOperar } = useAuth();

    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [archivo, setArchivo] = useState(null);
    const [lineas, setLineas] = useState([]);
    const [simulacion, setSimulacion] = useState(null);
    const [procesando, setProcesando] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const [soloSinPrecio, setSoloSinPrecio] = useState(false);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            const { data } = await api.get('/consignacion/precios');
            setDatos(data);
        } catch (err) {
            console.error('Error cargando los precios:', err);
            showToast(err.response?.data?.error || 'No se pudieron cargar los precios.', 'danger');
        } finally {
            setCargando(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const limpiarCarga = () => {
        setArchivo(null);
        setLineas([]);
        setSimulacion(null);
        const input = document.getElementById('file-precios');
        if (input) input.value = null;
    };

    const leerArchivo = async (event) => {
        event.preventDefault();
        if (!archivo) return;

        try {
            const filas = await parseExcelFile(archivo, { maxSizeMb: 10 });
            if (!filas.length) {
                showToast('El archivo está vacío.', 'warning');
                return;
            }

            const colCodigo = buscarColumna(filas[0], COL_CODIGO);
            const colPrecio = buscarColumna(filas[0], COL_PRECIO);

            if (!colCodigo || !colPrecio) {
                showToast(
                    `No se encontró la columna de ${!colCodigo ? 'código de artículo' : 'precio'}. ` +
                    `Descargá la plantilla para ver los encabezados que se reconocen.`,
                    'danger', 9000
                );
                return;
            }

            // Se cargan los artículos que trae el archivo, sin filtrarlos contra
            // los de consignación: el Excel es lo que decide qué se actualiza.
            const aCargar = filas
                .map(f => ({ codigo: String(f[colCodigo] ?? '').trim(), precio: f[colPrecio] }))
                .filter(l => l.codigo);

            setLineas(aCargar);

            // Cuántos son de consignación es sólo informativo, para que se note
            // si el archivo es el que se esperaba.
            const enConsignacion = new Set((datos?.articulos || []).map(a => a.codigo));
            const propias = aCargar.filter(l => enConsignacion.has(l.codigo)).length;

            showToast(
                `${aCargar.length} fila(s) leídas` +
                (propias !== aCargar.length ? ` · ${propias} son de consignación` : ''),
                'success', 7000
            );

            // Simulación: el backend dice qué precio entendió de cada fila.
            const { data } = await api.post('/consignacion/precios', { lineas: aCargar, simular: true });
            setSimulacion(data);
        } catch (err) {
            const detalle = err.response?.data;
            if (detalle?.invalidas) {
                showToast(
                    `${detalle.error} Ejemplos: ` +
                    detalle.invalidas.slice(0, 3).map(i => `${i.codigo} (${i.motivo})`).join(' · '),
                    'danger', 12000
                );
            } else {
                showToast(err.message || 'No se pudo leer el archivo.', 'danger');
            }
            setSimulacion(null);
        }
    };

    const aplicar = async () => {
        setProcesando(true);
        try {
            const { data } = await api.post('/consignacion/precios', { lineas });

            showToast(
                `${data.aplicados} precio(s) aplicados en la lista ${data.listaPrecio}` +
                (data.omitidas ? ` · ${data.omitidas} sin precio en el archivo` : '') +
                (data.totalErrores ? ` · ${data.totalErrores} con error` : ''),
                data.totalErrores ? 'warning' : 'success',
                9000
            );

            if (data.errores?.length) {
                console.warn('Errores al aplicar precios:', data.errores);
            }

            limpiarCarga();
            cargar();
        } catch (err) {
            showToast(err.response?.data?.error || 'No se pudieron aplicar los precios.', 'danger', 9000);
        } finally {
            setProcesando(false);
        }
    };

    const descargarPlantilla = () => {
        const wb = XLSX.utils.book_new();

        // Se precargan los artículos sin precio: es el caso de uso real, y evita
        // tener que copiar los códigos a mano desde otra pantalla.
        const sinPrecio = (datos?.articulos || []).filter(a => a.sinPrecio);
        const filas = (sinPrecio.length ? sinPrecio : (datos?.articulos || [])).map(a => ({
            'Número de artículo': a.codigo,
            'Descripción del artículo': a.descripcion,
            'Precio': ''
        }));

        const hoja = XLSX.utils.json_to_sheet(filas.length ? filas : [{
            'Número de artículo': 'C000948', 'Descripción del artículo': '', 'Precio': ''
        }]);
        hoja['!cols'] = [{ wch: 20 }, { wch: 55 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, hoja, 'Precios');

        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
            ['Carga de precios de consignación'],
            [],
            [`Los precios se aplican en la lista ${datos?.listaPrecio ?? 3} (${datos?.moneda || 'ARS'}).`],
            ['Es la lista que valoriza el stock y con la que sale la orden de reposición.'],
            [],
            ['CÓMO USAR'],
            ['1. Completá la columna Precio. La planilla ya viene con los artículos que no tienen precio.'],
            ['2. Subí el archivo: antes de escribir vas a ver qué precio se interpretó de cada fila.'],
            ['3. Recién ahí se aplica en SAP.'],
            [],
            ['SOBRE EL FORMATO DEL PRECIO'],
            ['Se aceptan las dos formas: 1.234,56 y 1,234.56, con o sin la moneda al final.'],
            ['Una celda vacía se saltea: no pone el precio en cero.'],
            [],
            ['COLUMNAS QUE SE RECONOCEN'],
            ['Código', COL_CODIGO.join(' / ')],
            ['Precio', COL_PRECIO.join(' / ')],
            [],
            ['Se cargan los artículos que traiga el archivo: podés armar una planilla con'],
            ['unos pocos o subir el listado completo que exporta SAP.']
        ]), 'Instrucciones');

        XLSX.writeFile(wb, 'Precios de consignacion.xlsx');
        showToast('Plantilla descargada.', 'success');
    };

    const articulos = (datos?.articulos || []).filter(a => {
        if (soloSinPrecio && !a.sinPrecio) return false;
        if (!busqueda.trim()) return true;
        const t = busqueda.trim().toLowerCase();
        return a.codigo.toLowerCase().includes(t)
            || a.descripcion.toLowerCase().includes(t)
            || (a.proveedorNombre || '').toLowerCase().includes(t);
    });

    if (cargando) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2">Cargando precios...</p>
            </div>
        );
    }

    if (!datos) return null;

    return (
        <div className="mt-4">
            <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="chip-resumen">
                    <i className="bi bi-tags"></i> {datos.total} artículos
                </span>
                <span className={`chip-resumen ${datos.sinPrecio > 0 ? 'semaforo-quiebre' : 'semaforo-ok'}`}>
                    <span className="punto" /> {datos.sinPrecio} sin precio
                </span>
                <span className="chip-resumen">
                    Lista {datos.listaPrecio} · {datos.moneda}
                </span>
            </div>

            {/* --- Carga --- */}
            {puedeOperar && (
                <div className="card shadow-sm mb-3">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                            <h5 className="card-title mb-0">Cargar precios desde Excel</h5>
                            <button className="btn btn-outline-primary btn-sm" onClick={descargarPlantilla}>
                                <i className="bi bi-download me-1"></i>
                                Descargar plantilla
                                {datos.sinPrecio > 0 && ` (${datos.sinPrecio} sin precio)`}
                            </button>
                        </div>

                        <form onSubmit={leerArchivo} className="row g-2 align-items-end">
                            <div className="col-md-8">
                                <input
                                    id="file-precios"
                                    type="file"
                                    className="form-control"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={e => { setArchivo(e.target.files[0]); setSimulacion(null); }}
                                />
                                <div className="form-text">
                                    Se cargan los artículos que traiga el archivo. Podés armar un Excel con
                                    unos pocos o subir el listado completo que exporta SAP.
                                </div>
                            </div>
                            <div className="col-md-4">
                                <button type="submit" className="btn btn-primary w-100" disabled={!archivo}>
                                    <i className="bi bi-search me-1"></i> Revisar archivo
                                </button>
                            </div>
                        </form>

                        {/* --- Previsualización de lo interpretado --- */}
                        {simulacion && (
                            <div className="mt-3">
                                <div className="alert alert-info py-2 px-3 mb-2 small d-flex flex-wrap gap-3">
                                    <span><strong>{simulacion.aCargar}</strong> a cargar</span>
                                    {simulacion.omitidas > 0 && (
                                        <span className="text-muted">
                                            <strong>{simulacion.omitidas}</strong> sin precio en el archivo (se saltean)
                                        </span>
                                    )}
                                    <span className="text-muted">lista {simulacion.listaPrecio}</span>
                                </div>

                                {simulacion.muestra?.length > 0 && (
                                    <>
                                        <div className="text-muted small mb-1">
                                            Así se interpretó cada precio. Revisalo antes de aplicar
                                            {simulacion.aCargar > simulacion.muestra.length &&
                                                ` (se muestran ${simulacion.muestra.length} de ${simulacion.aCargar})`}:
                                        </div>
                                        <div className="tabla-modal mb-3">
                                            <table className="table table-sm align-middle mb-0">
                                                <thead className="table-light sticky-top">
                                                    <tr>
                                                        <th style={{ width: '120px' }}>Código</th>
                                                        <th className="text-end" style={{ width: '160px' }}>Precio a aplicar</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {simulacion.muestra.map(l => (
                                                        <tr key={l.codigo}>
                                                            <td><code className="small">{l.codigo}</code></td>
                                                            <td className="text-end fw-semibold">{formatMoney(l.precio)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}

                                <div className="d-flex gap-2">
                                    <button className="btn btn-success" onClick={aplicar} disabled={procesando || !simulacion.aCargar}>
                                        {procesando
                                            ? 'Aplicando...'
                                            : `Aplicar ${simulacion.aCargar} precio(s) en SAP`}
                                    </button>
                                    <button className="btn btn-outline-secondary" onClick={limpiarCarga} disabled={procesando}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* --- Estado actual --- */}
            <div className="card shadow-sm">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <h5 className="card-title mb-0">Precios actuales</h5>
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                            <div className="form-check mb-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="soloSinPrecio"
                                    checked={soloSinPrecio}
                                    onChange={e => setSoloSinPrecio(e.target.checked)}
                                />
                                <label className="form-check-label small" htmlFor="soloSinPrecio">
                                    Sólo los que no tienen precio
                                </label>
                            </div>
                            <input
                                type="search"
                                className="form-control form-control-sm"
                                style={{ width: '240px' }}
                                placeholder="Buscar por código, artículo o proveedor..."
                                value={busqueda}
                                onChange={e => setBusqueda(e.target.value)}
                            />
                        </div>
                    </div>

                    {articulos.length === 0 ? (
                        <p className="text-muted mb-0">No hay artículos que coincidan con el filtro.</p>
                    ) : (
                        <div className="tabla-consignacion">
                            <table className="table table-sm table-hover align-middle">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th style={{ width: '100px' }}>Código</th>
                                        <th>Artículo</th>
                                        <th>Proveedor</th>
                                        <th className="text-end" style={{ width: '90px' }}>Stock</th>
                                        <th className="text-end" style={{ width: '150px' }}>Precio lista {datos.listaPrecio}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {articulos.map(a => (
                                        <tr key={a.codigo} className={a.sinPrecio ? 'table-warning' : undefined}>
                                            <td><code className="small">{a.codigo}</code></td>
                                            <td className="small celda-descripcion" title={a.descripcion}>{a.descripcion}</td>
                                            <td className="small text-muted">{a.proveedorNombre}</td>
                                            <td className="text-end text-muted">{formatNumber(a.stock)}</td>
                                            <td className="text-end">
                                                {a.sinPrecio
                                                    ? <span className="text-warning-emphasis fw-semibold">
                                                        <i className="bi bi-exclamation-triangle-fill me-1"></i>Sin precio
                                                      </span>
                                                    : <span className="fw-semibold">{formatMoney(a.precio)}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TabPrecios;
